<template>
  <form class="space-y-4">
    <!-- 🔸 Erreurs globales -->
    <div v-if="globalErrors.length" class="bg-red-100 text-red-700 p-2 rounded">
      <ul class="list-disc m-0">
        <li v-for="(err, i) in globalErrors" :key="'global-' + i">
          {{ err }}
        </li>
      </ul>
    </div>

    <div 
      v-for="(field, index) in visibleFields" 
      :key="index" 
      class="space-y-2"
    >
      <label class="block font-semibold" v-if="showlabels && !labels_disable.includes(field.input)">
        {{ field.label }} <template v-if="field.required">*</template>
      </label>

      <!-- Composant custom + many -->
      <template v-if="field.component && field.many">
        <div class="flex gap-2 items-center" v-for="(item, i) in formData[field.name || field.label]" :key="i">
          <component
            :is="internalComponents[field.component]"
            v-model="formData[field.name || field.label][i]"
            v-bind="field.props || {}"
            @update:modelValue="handleChange" />
          <button type="button" @click="eraseField(field.name || field.label, i)" class="text-red-500">✕</button>
        </div>
        <button type="button" class="text-sm text-blue-500 underline" @click="addField(field.name || field.label)">
          {{ gettext('ajouter' )}} {{ field.label }}
        </button>
      </template>

      <!-- Composant custom simple -->
      <template v-else-if="field.component">
        <component
          :is="internalComponents[field.component]"
          v-model="formData[field.name || field.label]"
          v-bind="field.props || {}"
          @update:object="handleEvent"
          @update:modelValue="handleChange" />
      </template>

      <!-- Input HTML standard + many -->
      <template v-else-if="field.many">
        <div class="flex gap-2 items-center" v-for="(item, i) in formData[field.name || field.label]" :key="i">
          <select v-if="field.input === 'select'"
            class="border p-2 rounded w-full"
            v-model="formData[field.name || field.label][i]"
            @change="handleChange">
            <option v-for="(option, idx) in get_options(field)" :key="idx" :value="option.value">
              {{ option.label || option.display_name }}
            </option>
          </select>

          <textarea v-else-if="field.input === 'textarea'"
            class="border p-2 rounded w-full"
            :rows="field.rows || 4"
            :placeholder="field.placeholder"
            v-model="formData[field.name || field.label][i]"
            @input="handleChange" />

          <input v-else
            class="border p-2 rounded w-full"
            :placeholder="field.placeholder"
            :type="field.input"
            v-model="formData[field.name || field.label][i]"
            @input="handleChange" />
          <button class="text-red-500" type="button" @click="eraseField(field.name || field.label, i)">✕</button>
        </div>
        <button type="button" class="text-sm text-blue-500 underline" @click="addField(field.name || field.label)">
          {{ gettext('ajouter') }} {{ field.label }}
        </button>
      </template>

      <!-- Input HTML simple -->
      <template v-else>
        <div class="flex gap-2 items-center">
        <label v-if="field.input === 'checkbox'">
          <input
            class="border p-2 rounded"
            :type="field.input"
            v-model="formData[field.name || field.label]"
            @input="handleChange" />
          <span class="pl-2">{{ field.label }}</span>
        </label>

        <select v-else-if="field.input === 'select'"
          class="border p-2 rounded w-full"
          v-model="formData[field.name || field.label]"
          @change="handleChange">
          <option v-for="(option, idx) in get_options(field)" :key="idx" :value="option.value">
            {{ option.label || option.display_name }}
          </option>
        </select>

        <textarea v-else-if="field.input === 'textarea'"
          class="border p-2 rounded w-full"
          :rows="field.rows || 4"
          :placeholder="field.placeholder"
          v-model="formData[field.name || field.label]"
          @input="handleChange" />

        <input v-else-if="field.input === 'hidden'" type="hidden" v-model="formData[field.name || field.label]" />

        <input v-else
          class="border p-2 rounded w-full"
          :name="field.name"
          :type="field.input"
          :placeholder="field.placeholder"
          v-model="formData[field.name || field.label]"
          @input="handleChange" />
            <button class="text-red-500" type="button" @click="eraseField(field.name || field.label)"
              v-if="!isRadioOrCheckboxField(field) && isFilledField(field)">✕</button>
          </div>
      </template>

      <!-- 🔸 Erreurs liées au champ -->
      <div
        v-if="errors[field.name || field.label] && errors[field.name || field.label].length"
        class="text-red-600 text-sm mt-1"
      >
        <ul class="list-disc pl-5 space-y-1">
          <li
            v-for="(err, i) in errors[field.name || field.label]"
            :key="(field.name || field.label) + '-err-' + i"
          >
            {{ err }}
          </li>
        </ul>
      </div>
    </div>

    <pre class="bg-gray-100 p-2 rounded" v-if="debugMode">{{ formData }}</pre>
  </form>
</template>

<script setup>
import { reactive, watch, computed } from 'vue'
import CustomSelect from './CustomSelect.vue'
import DateTime from './DateTime.vue'
import { gettext } from '../i18n'

const props = defineProps({
  addvalues: { type: Object, default: () => ({}) },
  config: Array,
  debugMode: { type: Boolean, default: false },
  values: { type: Object, default: () => ({}) },
  showlabels: { type: Boolean, default: true },
  addconfig: { type: Object, default: () => ({}) },
  only: { type: Array, default: () => [] },
  exclude: { type: Array, default: () => [] },
  order: { type: Array, default: () => [] },
  errors: { type: Object, default: () => ({}) } // 🆕 Ajout de la prop errors
})

const emit = defineEmits(['form-init', 'form-change', 'form-event'])

const internalComponents = { CustomSelect, DateTime }
const typeMapping = {
  string: 'text',
  number: 'number',
  boolean: 'checkbox',
  date: 'date',
  datetime: 'DateTime',
  choice: 'select',
  textarea: 'textarea',
}
const labels_disable = ['checkbox', 'radio']

// --- Config de base ---
let baseConfig = props.config.map(field => ({ ...field }))

if (props.addconfig && Object.keys(props.addconfig).length > 0) {
  baseConfig.forEach(field => {
    const key = field.name
    if (key && props.addconfig[key]) {
      const configToMerge = props.addconfig[key]
      Object.keys(configToMerge).forEach(propKey => {
        if (propKey === 'props') {
          field.props = { ...(field.props || {}), ...configToMerge.props }
        } else {
          field[propKey] = configToMerge[propKey]
        }
      })
    }
  })
}

if (props.only.length) baseConfig = baseConfig.filter(f => props.only.includes(f.name || f.label))
if (props.exclude.length) baseConfig = baseConfig.filter(f => !props.exclude.includes(f.name || f.label))

if (props.order.length) {
  const ordered = []
  const configMap = Object.fromEntries(baseConfig.map(f => [f.name || f.label, f]))
  props.order.forEach(key => {
    if (configMap[key]) { ordered.push(configMap[key]); delete configMap[key] }
  })
  baseConfig = [...ordered, ...Object.values(configMap)]
}

const reactiveConfig = reactive(baseConfig)

// --- Initialiser formData ---
const formData = reactive({ ...props.addvalues })

reactiveConfig.forEach(field => {
  const key = field.name || field.label
  let value = key in formData ? formData[key] : props.values[key]

  if (field.type === 'boolean' && value === undefined) value = false
  if (field.many) value = Array.isArray(value) ? [...value] : ['']
  if (value === undefined) value = null
  formData[key] = value

  if (!field.component && field.type && typeMapping[field.type]) {
    field.component = typeMapping[field.type]
    if (!internalComponents[field.component]) {
      field.input = field.component
      field.component = null
    }
  }
})

// --- Dépendances ---
function checkDeps(field) {
  if (!field?.deps) return true
  function validate(depsObj) {
    if (depsObj.or) return depsObj.or.some(cond => validate(cond))
    if (depsObj.and) return depsObj.and.every(cond => validate(cond))
    return Object.entries(depsObj).every(([depField, condition]) => {
      let value = formData[depField]
      if (typeof condition === 'boolean' && value === undefined) value = false
      if (condition === null) return value === null || value === undefined || value === ''
      if (condition === '!null') return value !== null && value !== undefined && value !== ''
      if (Array.isArray(condition)) return condition.includes(value)
      if (typeof condition === 'function') return condition(value, formData)
      return value === condition
    })
  }
  return validate(field.deps)
}

const visibleFields = computed(() => reactiveConfig.filter(f => checkDeps(f)))

function normalizeFields() {
  reactiveConfig.forEach(field => {
    const key = field.name || field.label
    const value = formData[key]

    // Si le champ est de type date ou datetime et vide, on remet à null
    if (
      ['date', 'datetime', 'datetime-local'].includes(field.input) &&
      (value === '' || value === undefined)
    ) {
      formData[key] = null
    }
  })
}

function isCheckBoxField(field) {
  return (field.input === 'checkbox') || (field.type === 'boolean')
}

function isRadioField(field) {
  return (field.input === 'radio') || (field.type === 'boolean')
}

function isRadioOrCheckboxField(field) {
  return isCheckBoxField(field) || isRadioField(field)
}

function isFilledField(field) {
  const key = field.name || field.label
  const value = formData[key]
  if (field.many) {
    return Array.isArray(value) && value.some(v => v !== null && v !== undefined && v !== '')
  }
  return value !== null && value !== undefined && value !== ''
}

// --- 🆕 Calcul des erreurs globales ---
const globalErrors = computed(() => {
  const fieldKeys = visibleFields.value.map(f => f.name || f.label)
  return Object.entries(props.errors)
    .filter(([key]) => !fieldKeys.includes(key))
    .flatMap(([, value]) => value || [])
})

// --- Fonctions utilitaires ---
function get_options(field) { return field.options || field.choices }
function addField(label) { if (formData[label]) formData[label].push('') }
function eraseField(label, index) { 
  if(index !== undefined){
    if (formData[label]?.length > 1) formData[label].splice(index, 1) 
  }else{
    delete formData[label]
  }
}
function handleEvent(event) { emit('form-event', event) }
function handleChange() {
   normalizeFields()
  emit('form-change', formData)
}

watch(formData, newData => {
  normalizeFields()
  emit('form-change', newData)
}, { deep: true })
emit('form-init', formData)
</script>
